import React from 'react';

/**
 * 前哨 AI 胜任力模型 · 三层八力结构图（首页 Hero 用）。
 *
 * 纯矢量 / 纯代码实现，替代原 competency-model.jpg 位图：
 *  - 8 个能力点沿半圆 165°→15° 每 21.43° 一个，左右镜像、均匀对称（数学保证）。
 *  - 三层能力环 + 玻璃质感中心球 + 顶部弧形标题，尽量还原原 jpg 观感。
 *  - 层名沿各自扇形弧度书写。
 * 边缘淡出交给 .v5-hero-fig svg 的 CSS 径向遮罩（与原 <img> 一致）。
 *
 * id 全部加 cm- 前缀，避免与页面其它 SVG 的 gradient/filter id 冲突。
 */

// 8 能力点：按 canonical 顺序（认知准备层→应用执行层→价值产出层），
// 坐标即沿半圆均匀对称排布后的落点。
const CHIPS = [
  { x: 278, y: 654, icon: 'cm-ic-brain', label: '认知理解力' },
  { x: 372, y: 457, icon: 'cm-ic-growth', label: '学习适应力' },
  { x: 532, y: 309, icon: 'cm-ic-target', label: '场景判断力' },
  { x: 734, y: 230, icon: 'cm-ic-chat', label: '指令交互力' },
  { x: 952, y: 230, icon: 'cm-ic-flow', label: '任务编排力' },
  { x: 1154, y: 309, icon: 'cm-ic-check', label: '结果评估力' },
  { x: 1314, y: 457, icon: 'cm-ic-shield', label: '安全责任力' },
  { x: 1408, y: 654, icon: 'cm-ic-innov', label: '创新转化力' },
];

// 三层能力环（左/顶/右）共用的扇形路径与高光路径
const LAYER_WEDGES = [
  'M388.3 789.1 A455 455 0 0 1 615.5 411 L688 536.5 A310 310 0 0 0 533.2 794.2 Z',
  'M629.4 403.3 A455 455 0 0 1 1056.6 403.3 L988.5 531.3 A310 310 0 0 0 697.5 531.3 Z',
  'M1070.5 411 A455 455 0 0 1 1297.7 789.1 L1152.8 794.2 A310 310 0 0 0 998 536.5 Z',
];
const LAYER_FILLS = ['url(#cm-leftLayer)', 'url(#cm-topLayer)', 'url(#cm-rightLayer)'];

// 内向刻度的旋转角
const TICK_ANGLES = [189, 212, 235, 258, 282, 305, 328, 351];

export default function CompetencyModelDiagram({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1686 980"
      role="img"
      aria-label="前哨 AI 胜任力模型 · 三层八力结构图"
    >
      <defs>
        <radialGradient id="cm-bgGlow" cx="50%" cy="74%" r="72%">
          <stop offset="0%" stopColor="#eaf3ff" />
          <stop offset="46%" stopColor="#f6faff" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cm-shellFill" x1="843" y1="60" x2="843" y2="860" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#eef5ff" />
          <stop offset="60%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f3f8ff" />
        </linearGradient>
        <linearGradient id="cm-outerBlue" x1="150" y1="690" x2="1536" y2="150" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#9cc2f7" />
          <stop offset="34%" stopColor="#1f74f4" />
          <stop offset="68%" stopColor="#0f5ee9" />
          <stop offset="100%" stopColor="#8fb8f5" />
        </linearGradient>

        {/* 认知准备层（左·蓝） */}
        <linearGradient id="cm-leftLayer" x1="400" y1="770" x2="690" y2="410" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#dcecff" />
          <stop offset="46%" stopColor="#5fb0ff" />
          <stop offset="100%" stopColor="#0b75f6" />
        </linearGradient>
        {/* 应用执行层（顶·强蓝） */}
        <linearGradient id="cm-topLayer" x1="650" y1="540" x2="1046" y2="360" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#0a6cf0" />
          <stop offset="52%" stopColor="#0b5cf4" />
          <stop offset="100%" stopColor="#0d79ff" />
        </linearGradient>
        {/* 价值产出层（右·紫） */}
        <linearGradient id="cm-rightLayer" x1="1000" y1="460" x2="1300" y2="800" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#5a44f6" />
          <stop offset="48%" stopColor="#7a60ff" />
          <stop offset="100%" stopColor="#cdddff" />
        </linearGradient>
        {/* 每层顶部高光（玻璃感） */}
        <linearGradient id="cm-layerGloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity=".55" />
          <stop offset="34%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>

        {/* 中心球 */}
        <radialGradient id="cm-coreFill" cx="42%" cy="34%" r="78%">
          <stop offset="0%" stopColor="#3a8cff" />
          <stop offset="34%" stopColor="#0d68f6" />
          <stop offset="74%" stopColor="#0a4fe0" />
          <stop offset="100%" stopColor="#0a47cf" />
        </radialGradient>
        <radialGradient id="cm-coreSpec" cx="40%" cy="26%" r="42%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity=".85" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cm-chipFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#eef4ff" />
        </linearGradient>

        {/* 中心球渐隐遮罩：基线以下（y≈800 起）淡出为透明，避免下半球太大 */}
        <linearGradient id="cm-coreFade" x1="0" y1="800" x2="0" y2="888" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fff" />
          <stop offset="1" stopColor="#000" />
        </linearGradient>
        <mask id="cm-coreMask">
          <rect x="0" y="0" width="1686" height="980" fill="url(#cm-coreFade)" />
        </mask>

        <filter id="cm-softShadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="9" stdDeviation="13" floodColor="#1b5ede" floodOpacity=".18" />
        </filter>
        <filter id="cm-chipShadow" x="-60%" y="-60%" width="220%" height="220%">
          <feDropShadow dx="0" dy="6" stdDeviation="9" floodColor="#2155c8" floodOpacity=".20" />
        </filter>
        <filter id="cm-textGlow" x="-25%" y="-25%" width="150%" height="150%">
          <feDropShadow dx="0" dy="3" stdDeviation="2.5" floodColor="#073ac1" floodOpacity=".30" />
        </filter>

        <path id="cm-titleArc" d="M225 477 A700 700 0 0 1 1461 477" />
        <path id="cm-arcTop" d="M733 444 A378 378 0 0 1 953 444" />
        <path id="cm-arcLeft" d="M484 688 A378 378 0 0 1 562 552" />
        <path id="cm-arcRight" d="M1124 552 A378 378 0 0 1 1202 688" />

        <symbol id="cm-ic-brain" viewBox="-28 -28 56 56">
          <path d="M-1-19C-7-22-15-20-17-13-23-12-25-5-21 0-24 4-22 11-16 12-16 17-9 19-3 16L-1 16Z" fill="#1268f2" />
          <path d="M1-19C7-22 15-20 17-13 23-12 25-5 21 0 24 4 22 11 16 12 16 17 9 19 3 16L1 16Z" fill="#1268f2" />
          <path d="M0-19V16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          <path d="M-10-8c-3 1-4 3-4 6m5 5c-3 1-4 3-3 6M10-8c3 1 4 3 4 6m-5 5c3 1 4 3 3 6" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
        </symbol>
        <symbol id="cm-ic-growth" viewBox="-28 -28 56 56">
          <rect x="-22" y="4" width="11" height="18" rx="2.2" fill="#1268f2" />
          <rect x="-5" y="-4" width="11" height="26" rx="2.2" fill="#1268f2" />
          <rect x="12" y="-14" width="11" height="36" rx="2.2" fill="#1268f2" />
          <path d="M-20-6L-7-15L0-11L15-22" fill="none" stroke="#1268f2" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6-22h9v9" fill="none" stroke="#1268f2" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
        </symbol>
        <symbol id="cm-ic-target" viewBox="-28 -28 56 56">
          <circle cx="0" cy="0" r="13" fill="none" stroke="#1268f2" strokeWidth="7" />
          <circle cx="0" cy="0" r="4" fill="#1268f2" />
          <path d="M-21-18v-6h12M21-18v-6H9M-21 18v6h12M21 18v6H9" fill="none" stroke="#1268f2" strokeWidth="5" strokeLinecap="round" />
          <path d="M-2-25h4M-2 25h4M-25-2v4M25-2v4" stroke="#1268f2" strokeWidth="4" strokeLinecap="round" />
        </symbol>
        <symbol id="cm-ic-chat" viewBox="-28 -28 56 56">
          <rect x="-23" y="-18" width="46" height="34" rx="6" fill="#1268f2" />
          <path d="M-10 16l-2 9 12-9z" fill="#1268f2" />
          <path d="M-13-6h26M-13 4h16" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" />
        </symbol>
        <symbol id="cm-ic-flow" viewBox="-28 -28 56 56">
          <rect x="-10" y="-24" width="20" height="14" rx="3" fill="#1268f2" />
          <rect x="-24" y="10" width="17" height="14" rx="3" fill="#1268f2" />
          <rect x="7" y="10" width="17" height="14" rx="3" fill="#1268f2" />
          <path d="M0-10V1M-15 10V1h30v9" fill="none" stroke="#1268f2" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        </symbol>
        <symbol id="cm-ic-check" viewBox="-28 -28 56 56">
          <circle cx="-3" cy="-4" r="18" fill="none" stroke="#1268f2" strokeWidth="6" />
          <path d="M-12-4l7 7L9-11" fill="none" stroke="#1268f2" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 12l12 12" stroke="#1268f2" strokeWidth="6" strokeLinecap="round" />
        </symbol>
        <symbol id="cm-ic-shield" viewBox="-28 -28 56 56">
          <path d="M0-25c7 5 15 6 23 7v14c0 13-8 22-23 28C-15 18-23 9-23-4v-14c8-1 16-2 23-7z" fill="#1268f2" />
          <rect x="-9" y="-2" width="18" height="17" rx="3" fill="#fff" />
          <path d="M-5-2v-5a5 5 0 0 1 10 0v5" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
          <circle cx="0" cy="6" r="2.2" fill="#1268f2" />
          <path d="M0 8v4" stroke="#1268f2" strokeWidth="2.4" strokeLinecap="round" />
        </symbol>
        <symbol id="cm-ic-innov" viewBox="-28 -28 56 56">
          <path d="M0-25c7 7 11 16 11 25v4H-11v-4c0-9 4-18 11-25z" fill="#1268f2" />
          <circle cx="0" cy="-8" r="5" fill="#fff" />
          <path d="M-11 2c-5 2-8 7-8 13 5 0 9-2 12-6zM11 2c5 2 8 7 8 13-5 0-9-2-12-6z" fill="#1268f2" />
          <path d="M-4 8h8l-4 12z" fill="#1268f2" />
        </symbol>
      </defs>

      <style>{`
        .cm-lbl{fill:#0b3bd0;font-size:25px;font-weight:700;}
        .cm-layer{fill:#fff;font-size:34px;font-weight:800;}
        .cm-copy{fill:#dce9ff;font-size:24px;font-weight:500;}
      `}</style>

      {/* 背景柔光 */}
      <rect width="1686" height="980" fill="url(#cm-bgGlow)" />

      {/* 外壳与圈层 */}
      <path d="M67 805 A776 776 0 0 1 1619 805 L1518 805 A675 675 0 0 0 168 805 Z" fill="url(#cm-shellFill)" opacity=".96" />
      <path d="M85 805 A758 758 0 0 1 1601 805" fill="none" stroke="url(#cm-outerBlue)" strokeWidth="10" strokeLinecap="round" />
      <path d="M168 805 A675 675 0 0 1 1518 805" fill="none" stroke="#d6e4ff" strokeWidth="2.2" />

      {/* 内向刻度 */}
      <g stroke="#8ab8ff" strokeWidth="1.4" strokeDasharray="5 9" opacity=".62">
        {TICK_ANGLES.map((a) => (
          <line key={a} x1="1320" y1="805" x2="1486" y2="805" transform={`rotate(${a} 843 805)`} />
        ))}
      </g>

      {/* 三层能力环 */}
      <g filter="url(#cm-softShadow)">
        {LAYER_WEDGES.map((d, i) => (
          <path key={i} d={d} fill={LAYER_FILLS[i]} stroke="#f6fbff" strokeWidth="4" strokeLinejoin="round" />
        ))}
      </g>
      {/* 层顶高光 */}
      <g opacity=".9" style={{ mixBlendMode: 'screen' }}>
        {LAYER_WEDGES.map((d, i) => (
          <path key={i} d={d} fill="url(#cm-layerGloss)" />
        ))}
      </g>

      {/* 内白环间隙 */}
      <path d="M543 805 A300 300 0 0 1 1143 805" fill="none" stroke="#eef4ff" strokeWidth="34" opacity=".95" />

      {/* 中心球（基线以下渐隐） */}
      <g mask="url(#cm-coreMask)">
        <circle cx="843" cy="805" r="248" fill="url(#cm-coreFill)" filter="url(#cm-softShadow)" />
        <circle cx="843" cy="805" r="230" fill="none" stroke="#9cc8ff" strokeWidth="2.6" strokeDasharray="1.6 6" opacity=".5" />
        <circle cx="843" cy="805" r="212" fill="none" stroke="#9cc8ff" strokeWidth="2" strokeDasharray="1.4 7.5" opacity=".3" />
        <ellipse cx="788" cy="720" rx="150" ry="96" fill="url(#cm-coreSpec)" opacity=".55" />
      </g>

      {/* 顶部弧形标题 */}
      <text fill="#0c44d8" fontSize="38" fontWeight="800" letterSpacing="7">
        <textPath href="#cm-titleArc" startOffset="50%" textAnchor="middle">从理解 AI，到协同 AI，再到转化 AI</textPath>
      </text>

      {/* 层名（沿扇形弧度书写） */}
      <g className="cm-layer" filter="url(#cm-textGlow)">
        <text><textPath href="#cm-arcLeft" startOffset="50%" textAnchor="middle">认知准备层</textPath></text>
        <text><textPath href="#cm-arcTop" startOffset="50%" textAnchor="middle">应用执行层</textPath></text>
        <text><textPath href="#cm-arcRight" startOffset="50%" textAnchor="middle">价值产出层</textPath></text>
      </g>
      {/* 层连接点 */}
      <g>
        <circle cx="494" cy="512" r="13" fill="#0b72f6" stroke="#eef6ff" strokeWidth="6" filter="url(#cm-softShadow)" />
        <circle cx="843" cy="350" r="13" fill="#0b72f6" stroke="#eef6ff" strokeWidth="6" filter="url(#cm-softShadow)" />
        <circle cx="1192" cy="512" r="13" fill="#6a59f7" stroke="#eef6ff" strokeWidth="6" filter="url(#cm-softShadow)" />
      </g>

      {/* 8 能力点（均匀对称：θ=165°→15°，每 21.43°一个） */}
      <g textAnchor="middle">
        {CHIPS.map((c) => (
          <g key={c.label} transform={`translate(${c.x} ${c.y})`}>
            <circle r="47" fill="url(#cm-chipFill)" stroke="#e3edff" strokeWidth="1.5" filter="url(#cm-chipShadow)" />
            <use href={`#${c.icon}`} width="54" height="54" x="-27" y="-29" />
            <text className="cm-lbl" y="92">{c.label}</text>
          </g>
        ))}
      </g>

      {/* 中心文案 */}
      <g textAnchor="middle">
        <text x="843" y="678" fill="#fff" fontSize="54" fontWeight="900" filter="url(#cm-textGlow)">前哨 AI</text>
        <text x="843" y="742" fill="#fff" fontSize="54" fontWeight="900" filter="url(#cm-textGlow)">胜任力模型</text>
        <text x="843" y="788" className="cm-copy">个人测评 ｜ 人才盘点 ｜ 能力培养</text>
      </g>
    </svg>
  );
}
